import { useMutation } from '@tanstack/react-query';
import { CircleCheck, Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Field, Input, Modal, Textarea } from './ui';

const EMPTY = { system: '', website: '', details: '' };

/** The last card on the Integrations page: asks Edvora for an integration it doesn't have yet. */
export function RequestIntegrationCard() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group flex min-h-48 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 p-5 text-center transition hover:border-brand-400 hover:bg-white focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:outline-none"
      >
        <span className="flex size-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
          <Plus className="size-6" />
        </span>
        <span className="mt-4 block text-lg leading-snug font-semibold text-slate-900">Want to see a new integration?</span>
        <span className="mt-1 block text-sm font-semibold text-brand-700 group-hover:underline">Let us know</span>
      </button>
      <RequestIntegrationModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function RequestIntegrationModal({ open, onClose }: { open: boolean; onClose(): void }) {
  const { me } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [sent, setSent] = useState(false);

  const send = useMutation({
    mutationFn: () => api.post('/integration-requests', { system: form.system, website: form.website, details: form.details }),
    onSuccess: () => setSent(true),
  });

  const close = () => {
    onClose();
    // After the modal has gone, so it doesn't flash back to the form.
    setTimeout(() => {
      setForm(EMPTY);
      setSent(false);
      send.reset();
    }, 200);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    send.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      dismissible={!send.isPending}
      title={sent ? 'Thanks for letting us know' : 'Request an integration'}
      description={sent ? undefined : 'Tell the Edvora team what you’d like Edvora to connect to. Requests go to Edvora, not to your school’s administrators.'}
      footer={
        sent ? (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={send.isPending}>
              Cancel
            </Button>
            <Button type="submit" form="integration-request" variant="primary" loading={send.isPending}>
              Send request
            </Button>
          </>
        )
      }
    >
      {sent ? (
        <div className="flex items-start gap-3 text-sm text-slate-600">
          <CircleCheck className="mt-0.5 size-5 shrink-0 text-emerald-600" />
          <p>
            We’ve passed your request for <strong className="text-slate-900">{form.system.trim()}</strong> to the Edvora team. If we have questions, we’ll
            write to {me?.user.email}.
          </p>
        </div>
      ) : (
        <form id="integration-request" onSubmit={submit} className="space-y-4">
          {send.error && <Alert tone="red">{errorMessage(send.error)}</Alert>}
          <Field label="What should Edvora connect to?" required>
            <Input
              autoFocus
              required
              minLength={2}
              maxLength={120}
              value={form.system}
              onChange={(event) => setForm({ ...form, system: event.target.value })}
              placeholder="e.g. Destiny Library Manager, Raptor, Canvas"
            />
          </Field>
          <Field label="Website" hint="Optional. Where we can learn about it.">
            <Input
              inputMode="url"
              autoComplete="url"
              maxLength={300}
              value={form.website}
              onChange={(event) => setForm({ ...form, website: event.target.value })}
              placeholder="e.g. https://www.follettsoftware.com"
            />
          </Field>
          <Field label="What would it do for your school?" required hint="What should come into Edvora, or go out of it, and how you’d use it.">
            <Textarea
              required
              minLength={10}
              maxLength={4000}
              rows={5}
              value={form.details}
              onChange={(event) => setForm({ ...form, details: event.target.value })}
            />
          </Field>
          <p className="text-xs text-slate-500">
            Sent as {me?.user.name} ({me?.user.email}){me?.tenant ? ` from ${me.tenant.name}` : ''}.
          </p>
        </form>
      )}
    </Modal>
  );
}
